"use client"

// P-1 (audit 2026-05-28): icon-only buttons used to render as 24×24 or
// 28×28 hit areas in dense admin rows — sub-44px, fails WCAG 2.5.5.
// IconButton wraps the visual in a ≥44×44 button so the tap area is
// always finger-sized; the icon itself stays small for visual density.

import type { ComponentPropsWithoutRef, ReactNode } from "react"

export type IconButtonTone = "default" | "danger" | "success" | "warning"
export type IconButtonSize = "sm" | "md"
export type IconButtonVariant = "ghost" | "subtle"

type Props = Omit<ComponentPropsWithoutRef<"button">, "children"> & {
  icon: ReactNode
  /** Required for screen readers — IconButton has no visible text. */
  "aria-label": string
  tone?: IconButtonTone
  size?: IconButtonSize
  variant?: IconButtonVariant
}

const TONE_CLS: Record<IconButtonTone, string> = {
  default: "text-[var(--redy-secondary)] hover:text-[var(--redy-charcoal)]",
  danger: "text-[var(--redy-error)] hover:text-[var(--redy-red-deep)]",
  success: "text-[var(--redy-success)] hover:opacity-80",
  warning: "text-[var(--redy-amber-deep)] hover:opacity-80",
}

const SIZE_ICON: Record<IconButtonSize, string> = {
  sm: "[&>svg]:w-4 [&>svg]:h-4",
  md: "[&>svg]:w-5 [&>svg]:h-5",
}

const VARIANT_CLS: Record<IconButtonVariant, string> = {
  ghost: "bg-transparent hover:bg-[var(--redy-warm-white)]",
  subtle: "bg-[var(--redy-warm-white)] hover:bg-[var(--redy-border-l)]",
}

const BASE_CLS =
  "inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-[10px] " +
  "transition-colors duration-150 ease-out shrink-0 " +
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--redy-red)]"

export function IconButton({
  icon,
  tone = "default",
  size = "md",
  variant = "ghost",
  className = "",
  ...rest
}: Props) {
  const cls = [
    BASE_CLS,
    TONE_CLS[tone],
    SIZE_ICON[size],
    VARIANT_CLS[variant],
    className,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <button type="button" className={cls} {...rest}>
      {icon}
    </button>
  )
}
